import { Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatDialogRef } from '@angular/material/dialog';
import { MatStepper } from '@angular/material/stepper';
import { TranslateService } from '@ngx-translate/core';
import { Actions, Select, Store, ofActionDispatched } from '@ngxs/store';
import { SearchAllModules } from 'app/library/all-modules/actions';
import { AllModulesState } from 'app/library/all-modules/models/all-modules.state';
import { Feature } from 'app/library/feature/models/feature.model';
import { FeatureState } from 'app/library/feature/models/feature.state';
import { FeatureService } from 'app/library/feature/services/feature.service';
import { ModuleUserPermissions } from 'app/library/module-user/enums/module-user-permissions.enum';
import { ModuleManagerPermissions } from 'app/library/module-user/models/module-manager-permissions';
import { CreateModuleSuccess } from 'app/library/module/actions';
import { ModuleKind } from 'app/library/module/enums/module-kind.enum';
import { ModuleStatuses } from 'app/library/module/models/module-statuses';
import { Module } from 'app/library/module/models/module.model';
import { FetchTags } from 'app/library/tag/actions';
import { TagMultiSelectService } from 'app/library/tag/services/multi-select.service';
import { TagService } from 'app/library/tag/services/tag.service';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { fuseAnimations } from 'app/projects/fuse/src/lib/animations';
import { IStatus } from 'app/projects/shared/src/lib/interfaces/status.interface';
import { User } from 'app/projects/user/src/lib/models/user';
import { FeatureSwitchName } from 'app/projects/shared/src/lib/enums/feature-switch.enum';
import { UserState } from 'app/projects/user/src/lib/models/user.state';
import { BehaviorSubject, Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-take-over-users-to-modules',
    templateUrl: './take-over-users-to-modules.component.html',
    styleUrls: ['./take-over-users-to-modules.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations: fuseAnimations,
})
export class TakeOverUsersToModulesDialogComponent extends UnsubscribeOnDestroy implements OnInit {
    private static readonly BOOKING_CONTEXT = 'booking';
    @Select(AllModulesState.getFilteredEntities)
    private _filteredModule$: Observable<Module[]>;
    filteredModules: Module[];

    private _filteredModules = new BehaviorSubject<Module[]>([]);

    filteredModule$ = this._filteredModules.asObservable().pipe(
        filter((modules) => !!modules),
        map((modules) =>
            modules.filter((tempModule) => {
                for (const selectedTag of this.tagMultiSelectService.selectedTags) {
                    if (tempModule.tags.map((tag) => tag.id).indexOf(selectedTag.id) === -1) {
                        return false;
                    }
                }

                return true;
            })
        )
    );

    @Select(AllModulesState.getEntities)
    module$: Observable<Module[]>;

    @Select(AllModulesState.getSelectedEntityIds)
    selectedModuleId$: Observable<string[]>;

    @Select(AllModulesState.getTotalCount)
    totalCount$: Observable<number>;

    @Select(UserState.getMyInfo)
    profile$: Observable<User>;
    profile: User;

    @Select(FeatureState.getEntities)
    feature$: Observable<Feature[]>;
    features: Feature[];

    @Select(AllModulesState.getSearchTerm)
    searchTerm$: Observable<string>;

    private _loadModules = new BehaviorSubject<boolean>(null);
    loadModule$ = this._loadModules.asObservable();

    private _statuse$ = new BehaviorSubject<IStatus>(ModuleStatuses.all.filter((status) => status.name === sessionStorage.getItem('allModulesSelectedStatus'))[0]);
    statuse$ = this._statuse$.asObservable();

    private _removeModuleFromList = new BehaviorSubject<Module>(null);
    removeModuleFromList$ = this._removeModuleFromList.asObservable();

    @Input()
    title: string;

    @Input()
    filteredUserIds: string[] = [];

    get permissionsFormArray(): FormArray {
        return this.formGroup.get('permissions') as FormArray;
    }

    get isPositionSelected(): boolean {
        return (
            this.formGroup.controls.userRole.value === 'participant' ||
            this.formGroup.controls.userRole.value === 'manager' ||
            this.formGroup.controls.userRole.value === 'responsible'
        );
    }

    getSelectedPermissions(): ModuleUserPermissions[] {
        return this.permissionsFormArray.controls
            .map((control, i) => (control.value ? this.moduleManagerPermissions.all[i].value : null))
            .filter((value): value is ModuleUserPermissions => value !== null);
    }

    getUserInputControl(index: number): FormControl {
        return (this.formGroup.get('userInputs') as FormArray).at(index) as FormControl;
    }

    statusCtrl = new FormControl();
    statuses = ModuleStatuses.all.filter((status) => ModuleStatuses.visibleKeys.includes(status.name));

    searchInput = new FormControl();
    moduleList: Module[] = [];
    removable = true;
    displayedColumns = ['avatar', 'name', 'version', 'type', 'startDate', 'endDate', 'status', 'actions'];

    ModuleKind = ModuleKind;
    formGroup: FormGroup;
    moduleManagerPermissions = ModuleManagerPermissions;
    isCourseCatalogFeatureEnabled: boolean;

    constructor(
        private _store: Store,
        private _action$: Actions,
        public tagService: TagService,
        public tagMultiSelectService: TagMultiSelectService,
        private fb: FormBuilder,
        private _translateService: TranslateService,
        private _featureService: FeatureService,
        public dialogRef: MatDialogRef<TakeOverUsersToModulesDialogComponent>
    ) {
        super();

        this.isCourseCatalogFeatureEnabled = this._featureService.checkFeatureStatus(FeatureSwitchName.CourseCatalog);
    }

    ngOnInit(): void {
        this._store.dispatch([new FetchTags(), new SearchAllModules({ searchItem: '', sessionStorageKey: 'allModulesSearchTerm' })]);

        this.formGroup = this.fb.group({
            userRole: 'isParticipant',
            permissions: this.fb.array([]),
            userInputs: this.fb.array(this.filteredUserIds.map(() => new FormControl(''))),
        });

        this.addPermissionsCheckboxes();

        this.statusCtrl.setValue(this._statuse$.value ? this._statuse$.value.name : '');

        this.feature$
            .pipe(
                filter((features) => !!features),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe((features) => {
                this.features = features;
            });

        this._action$.pipe(ofActionDispatched(CreateModuleSuccess), takeUntil(this._unsubscribeAll)).subscribe(() => {
            this._loadModules.next(false);
        });

        this.tagMultiSelectService.selectedTag$
            .pipe(
                filter((tags) => !!tags),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe((tags) => {
                this._updateMyModules();
            });

        this._filteredModule$
            .pipe(
                filter((modules) => !!modules),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe((modules) => {
                this.filteredModules = modules;
                this._updateMyModules();
            });

        this.searchInput.valueChanges.pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this._unsubscribeAll)).subscribe((searchTerm) => {
            this._store.dispatch(new SearchAllModules({ searchItem: searchTerm, sessionStorageKey: 'allModulesSearchTerm' }));
        });

        this.profile$
            .pipe(
                filter((profile) => !!profile),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe((profile) => {
                this.profile = profile;
            });
    }

    private _updateMyModules(): void {
        this._filteredModules.next(this.filteredModules);
    }

    statusSelected(event: MatAutocompleteSelectedEvent): void {
        this._statuse$.next(ModuleStatuses.all.filter((status) => status.name === this.statusCtrl.value)[0]);

        sessionStorage.setItem('allModulesSelectedStatus', this.statusCtrl.value || '');
    }

    private addPermissionsCheckboxes(): void {
        this.moduleManagerPermissions.all.forEach(() => this.permissionsFormArray.push(new FormControl(true)));
    }

    updateList(list: Module[]): void {
        this.moduleList = list;
    }

    removeFromList(module: Module): void {
        this.moduleList = [...this.moduleList.filter((item) => item.id !== module.id)];

        this._removeModuleFromList.next(module);
    }

    goForward(stepper: MatStepper): void {
        stepper.next();
    }

    async onStepChange(stepper: MatStepper): Promise<void> {
        if (stepper.selectedIndex === 0) {
            this.dialogRef.updateSize('680px');
            this.title = this._translateService.instant('LIST.USER_MODULES.FIELD.CHOOSE_POSITION.TITLE', {
                usersCount: this.filteredUserIds.length,
            });
        } else if (stepper.selectedIndex === 1) {
            this.dialogRef.updateSize('1024px');
            this.title = this._translateService.instant('LIST.USER_MODULES.FIELD.ASSIGN_TO.TITLE', {
                usersCount: this.filteredUserIds.length,
            });
            // Ensure modules are fetched when opening the Course step (SearchAllModules only updates search term).
            this._loadModules.next(true);
        }
    }

    saveSelected(): void {
        const roles = this.filteredUserIds.map((userId, index) => ({
            user_id: userId,
            role: this.formGroup.controls.userInputs.value[index],
        }));

        this.dialogRef.close({
            data: {
                userRole: this.formGroup.controls.userRole.value,
                moduleList: this.moduleList,
                permissions: this.getSelectedPermissions(),
                roles: roles,
            },
        });
    }

    get modulesContext(): string {
        return this.isCourseCatalogFeatureEnabled ? TakeOverUsersToModulesDialogComponent.BOOKING_CONTEXT : null;
    }
}
